import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import toast from "react-hot-toast";

/* ─── SVG Icons ─── */
const HomeIcon = ({ filled }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const MenuDrinkIcon = ({ filled }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 8h1a4 4 0 0 1 0 8h-1" />
    <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z" />
    <line x1="6" y1="2" x2="6" y2="4" />
    <line x1="10" y1="2" x2="10" y2="4" />
    <line x1="14" y1="2" x2="14" y2="4" />
  </svg>
);
const CartIcon = ({ filled }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);
const OrderIcon = ({ filled }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);
const UserIcon = ({ filled }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const ContactIcon = ({ filled }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const ChevronDown = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const LogoutIco = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const AdminIco = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M12 2v2M12 20v2M20 12h2M2 12h2M17.66 17.66l-1.41-1.41M6.34 17.66l1.41-1.41" />
  </svg>
);

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, cartCount } = useApp();

  const [menuOpen, setMenuOpen] = useState(false);
  const [cartBounce, setCartBounce] = useState(false);
  const [prevCount, setPrevCount] = useState(cartCount);
  const menuRef = useRef(null);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  useEffect(() => {
    if (cartCount > prevCount) {
      setCartBounce(true);
      setTimeout(() => setCartBounce(false), 600);
    }
    setPrevCount(cartCount);
  }, [cartCount]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    toast.success("See you soon! 👋");
    navigate("/");
  };

  const initials = user?.name
    ? user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : user?.phone?.slice(-2) || "?";

  const isProfileIncomplete = user && (!user.name || !user.email || !user.address?.street);

  /* ── bottom-tab config ── */
  const bottomTabs = [
    { label: "Home",    path: "/",        Icon: HomeIcon       },
    { label: "Menu",    path: "/products",Icon: MenuDrinkIcon  },
    { label: "Contact", path: "/contact", Icon: ContactIcon    },
    { label: "Cart",    path: "/cart",    Icon: CartIcon, badge: cartCount },
    ...(user
      ? [
          { label: "Orders",  path: "/orders",  Icon: OrderIcon },
          { label: "Profile", path: "/profile", Icon: UserIcon  },
        ]
      : [{ label: "Login", path: "/login", Icon: UserIcon }]
    ),
  ];

  return (
    <>
      {/* ══ TOP NAVBAR ══ */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 50 }}>

        {/* profile nudge */}
        {isProfileIncomplete && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "5px 16px", background: "linear-gradient(90deg, #f59e0b, #fb923c)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
              <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>Profile incomplete!</span>
              <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                · Missing:{" "}
                {[!user?.name && "name", !user?.email && "email", !user?.address?.street && "address"].filter(Boolean).join(", ")}
              </span>
            </div>
            <button onClick={() => navigate("/profile")}
              style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 100, background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.38)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}>
              Update →
            </button>
          </div>
        )}

        {/* main nav bar */}
        <nav style={{ background: "rgba(255,255,255,0.96)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 1px 10px rgba(0,0,0,0.05)" }}>
          <div style={{ maxWidth: 1152, margin: "0 auto", padding: "0 20px", height: 64, display: "flex", alignItems: "center", gap: 10 }}>

            {/* Logo */}
            <button onClick={() => navigate("/")}
              style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: "4px 6px 4px 0", borderRadius: 10, transition: "opacity 0.15s", marginRight: "auto" }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}>
              <img src="https://res.cloudinary.com/proteinspot/image/upload/v1774009667/logo-transparent_tsfxmf.png" alt="ProteinSpot"
                style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0, objectFit: "contain" }} />
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ fontWeight: 900, fontSize: 17, letterSpacing: "-0.4px", color: "#0f2e18" }}>
                  Protein<span style={{ color: "#2D8B4E" }}>Spot</span>
                </div>
                <div style={{ fontSize: 9, fontWeight: 600, color: "#9ca3af", letterSpacing: "1.2px", textTransform: "uppercase" }}>
                  Fresh &amp; Natural
                </div>
              </div>
            </button>

            {/* ── Desktop only ── */}
            <div className="desk-only" style={{ display: "flex", alignItems: "center", gap: 8 }}>

              {/* nav pill group — Home · Menu · Contact */}
              <div style={{ display: "flex", alignItems: "center", gap: 2, background: "#f5f5f5", padding: "3px", borderRadius: 100, border: "1px solid rgba(0,0,0,0.07)" }}>
                {[
                  { label: "Home",    path: "/"        },
                  { label: "Menu",    path: "/products"},
                  { label: "Contact", path: "/contact" },
                ].map((link) => {
                  const active = isActive(link.path);
                  return (
                    <button key={link.path} onClick={() => navigate(link.path)}
                      style={{ padding: "7px 18px", borderRadius: 100, border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: active ? 700 : 600, background: active ? "#fff" : "transparent", color: active ? "#2D8B4E" : "#6b7280", boxShadow: active ? "0 1px 5px rgba(0,0,0,0.1)" : "none", transition: "all 0.2s ease" }}
                      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = "rgba(45,139,78,0.07)"; e.currentTarget.style.color = "#2D8B4E"; } }}
                      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#6b7280"; } }}>
                      {link.label}
                    </button>
                  );
                })}
              </div>

              {/* cart */}
              <button onClick={() => navigate("/cart")}
                style={{ position: "relative", display: "flex", alignItems: "center", gap: 6, padding: "8px 16px 8px 12px", borderRadius: 100, border: "1.5px solid", borderColor: isActive("/cart") ? "rgba(45,139,78,0.35)" : "rgba(0,0,0,0.09)", background: isActive("/cart") ? "rgba(45,139,78,0.07)" : "#fff", color: isActive("/cart") ? "#2D8B4E" : "#374151", fontSize: 13.5, fontWeight: 600, cursor: "pointer", transition: "all 0.2s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(45,139,78,0.35)"; e.currentTarget.style.background = "rgba(45,139,78,0.06)"; e.currentTarget.style.color = "#2D8B4E"; }}
                onMouseLeave={(e) => { const a = isActive("/cart"); e.currentTarget.style.borderColor = a ? "rgba(45,139,78,0.35)" : "rgba(0,0,0,0.09)"; e.currentTarget.style.background = a ? "rgba(45,139,78,0.07)" : "#fff"; e.currentTarget.style.color = a ? "#2D8B4E" : "#374151"; }}>
                <span style={{ animation: cartBounce ? "cartBounce 0.55s cubic-bezier(0.34,1.56,0.64,1)" : "none", display: "inline-block", fontSize: 16 }}>🛒</span>
                <span>Cart</span>
                {cartCount > 0 && (
                  <span style={{ position: "absolute", top: 3, right: 5, background: "linear-gradient(135deg, #FF6B35, #ff8f5f)", color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff", animation: cartBounce ? "scalePop 0.4s ease" : "none" }}>
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </button>

              {/* user avatar / login */}
              {user ? (
                <div style={{ position: "relative" }} ref={menuRef}>
                  <button onClick={() => setMenuOpen((o) => !o)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 11px 5px 5px", borderRadius: 100, border: `1.5px solid ${menuOpen ? "#2D8B4E" : "rgba(45,139,78,0.22)"}`, background: menuOpen ? "rgba(45,139,78,0.06)" : "#fff", cursor: "pointer", boxShadow: menuOpen ? "0 0 0 3px rgba(45,139,78,0.1)" : "none", transition: "all 0.2s ease" }}
                    onMouseEnter={(e) => { if (!menuOpen) { e.currentTarget.style.borderColor = "#2D8B4E"; e.currentTarget.style.background = "rgba(45,139,78,0.04)"; } }}
                    onMouseLeave={(e) => { if (!menuOpen) { e.currentTarget.style.borderColor = "rgba(45,139,78,0.22)"; e.currentTarget.style.background = "#fff"; } }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, #2D8B4E, #4CAF72)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 900, flexShrink: 0, boxShadow: "0 1px 4px rgba(45,139,78,0.3)" }}>
                      {initials}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {user.name?.split(" ")[0] || "Account"}
                    </span>
                    <span style={{ color: "#9ca3af", display: "flex", transition: "transform 0.2s", transform: menuOpen ? "rotate(180deg)" : "none" }}>
                      <ChevronDown />
                    </span>
                  </button>

                  {menuOpen && (
                    <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 216, background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.05)", overflow: "hidden", zIndex: 100, animation: "dropIn 0.17s cubic-bezier(0.34,1.4,0.64,1)" }}>
                      <div style={{ padding: "13px 16px 12px", borderBottom: "1px solid #f3f4f6", background: "linear-gradient(135deg, rgba(45,139,78,0.04), rgba(76,175,114,0.03))" }}>
                        <p style={{ fontWeight: 800, fontSize: 14, color: "#111827", margin: 0, letterSpacing: "-0.2px" }}>{user.name || "Hi there!"}</p>
                        <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0", fontFamily: "monospace" }}>{user.phone}</p>
                      </div>

                      {[
                        { label: "My Profile",  path: "/profile", icon: <UserIcon />    },
                        { label: "My Orders",   path: "/orders",  icon: <OrderIcon />   },
                        { label: "Contact Us",  path: "/contact", icon: <ContactIcon /> },
                        ...(user.role === "admin"
                          ? [{ label: "Admin Panel", path: "/admin", icon: <AdminIco /> }]
                          : []),
                      ].map((item) => (
                        <button key={item.path} onClick={() => { navigate(item.path); setMenuOpen(false); }}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13.5, fontWeight: 600, color: "#374151", textAlign: "left", transition: "all 0.1s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.color = "#2D8B4E"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#374151"; }}>
                          <span style={{ color: "#9ca3af", display: "flex" }}>{item.icon}</span>
                          {item.label}
                        </button>
                      ))}

                      <div style={{ height: 1, background: "#f3f4f6", margin: "2px 0" }} />
                      <button onClick={handleLogout}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13.5, fontWeight: 600, color: "#ef4444", textAlign: "left", transition: "background 0.1s", marginBottom: 4 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#fff5f5")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                        <LogoutIco /> Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => navigate("/login")}
                  style={{ padding: "9px 22px", borderRadius: 100, border: "none", cursor: "pointer", background: "linear-gradient(135deg, #2D8B4E, #4CAF72)", color: "#fff", fontSize: 13.5, fontWeight: 700, boxShadow: "0 4px 16px rgba(45,139,78,0.32)", transition: "all 0.2s ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1.5px)"; e.currentTarget.style.boxShadow = "0 7px 24px rgba(45,139,78,0.44)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(45,139,78,0.32)"; }}>
                  Login
                </button>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* top spacer */}
      <div className="top-spacer" style={{ height: isProfileIncomplete ? 88 : 64 }} />

      {/* ══ BOTTOM TAB BAR (mobile only) ══ */}
      <div className="mob-nav" style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50, display: "none" }}>
        <div style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(24px)", borderTop: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 -2px 20px rgba(0,0,0,0.08)", paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div style={{ display: "flex", height: 60 }}>
            {bottomTabs.map((tab) => {
              const active = isActive(tab.path);
              const isCartTab = tab.path === "/cart";
              return (
                <button key={tab.path} onClick={() => navigate(tab.path)}
                  style={{ flex: 1, border: "none", background: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, position: "relative", padding: "0 4px", WebkitTapHighlightColor: "transparent", outline: "none" }}>
                  <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: active ? 22 : 0, height: 2.5, background: "linear-gradient(90deg, #2D8B4E, #4CAF72)", borderRadius: "0 0 3px 3px", transition: "width 0.3s cubic-bezier(0.34,1.4,0.64,1)" }} />
                  <div style={{ position: "relative", color: active ? "#2D8B4E" : "#9ca3af", transition: "all 0.22s cubic-bezier(0.34,1.4,0.64,1)", transform: active ? "scale(1.18) translateY(-1px)" : "scale(1)" }}>
                    <tab.Icon filled={active} />
                    {isCartTab && cartCount > 0 && (
                      <span style={{ position: "absolute", top: -5, right: -7, background: "linear-gradient(135deg, #FF6B35, #ff8f5f)", color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff", animation: cartBounce ? "scalePop 0.4s ease" : "none" }}>
                        {cartCount > 9 ? "9+" : cartCount}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: active ? "#2D8B4E" : "#9ca3af", lineHeight: 1, letterSpacing: "0.1px", transition: "color 0.2s" }}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>


      <style>{`
        @keyframes dropIn {
          from { opacity:0; transform:translateY(-8px) scale(0.97); }
          to   { opacity:1; transform:translateY(0)    scale(1); }
        }
        @keyframes cartBounce {
          0%   { transform: scale(1) rotate(0); }
          25%  { transform: scale(1.3) rotate(-10deg); }
          55%  { transform: scale(0.9) rotate(5deg); }
          80%  { transform: scale(1.06) rotate(-2deg); }
          100% { transform: scale(1) rotate(0); }
        }
        @keyframes scalePop {
          0%   { transform: scale(0.5); }
          70%  { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        @media (min-width: 768px) {
          .desk-only  { display: flex  !important; }
          .mob-nav        { display: none  !important; }
          .top-spacer { display: block !important; }
        }
        @media (max-width: 767px) {
          .desk-only      { display: none  !important; }
          .mob-nav        { display: block !important; }
          .top-spacer     { height: ${isProfileIncomplete ? "88px" : "64px"} !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;